import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import styled from 'styled-components';

const SUBSCRIPTION_PLATFORMS = [
  { id: 8,    name: 'Netflix' },
  { id: 9,    name: 'Amazon Prime Video' },
  { id: 337,  name: 'Disney+' },
  { id: 350,  name: 'Apple TV+' },
  { id: 384,  name: 'Max' },
  { id: 15,   name: 'Hulu' },
  { id: 386,  name: 'Peacock' },
  { id: 531,  name: 'Paramount+' },
  { id: 1969, name: 'YouTube TV' },
  { id: 11,   name: 'Mubi' },
  { id: 258,  name: 'Criterion Channel' },
];

const FREE_PLATFORMS = [
  { id: 73,  name: 'Tubi' },
  { id: 300, name: 'Pluto TV' },
  { id: 207, name: 'The Roku Channel' },
  { id: 538, name: 'Plex' },
  { id: 613, name: 'Amazon Freevee' },
  { id: 192, name: 'YouTube Free' },
];

const STREAMING_PLATFORMS = [...SUBSCRIPTION_PLATFORMS, ...FREE_PLATFORMS];

const MPAA_RATINGS = ['G', 'PG', 'PG-13', 'R', 'NC-17'];

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'zh', name: 'Chinese' },
  { code: 'hi', name: 'Hindi' },
];

const GENRES = [
  { id: 28,    name: 'Action' },
  { id: 12,    name: 'Adventure' },
  { id: 16,    name: 'Animation' },
  { id: 35,    name: 'Comedy' },
  { id: 80,    name: 'Crime' },
  { id: 99,    name: 'Documentary' },
  { id: 18,    name: 'Drama' },
  { id: 10751, name: 'Family' },
  { id: 14,    name: 'Fantasy' },
  { id: 36,    name: 'History' },
  { id: 27,    name: 'Horror' },
  { id: 10402, name: 'Music' },
  { id: 9648,  name: 'Mystery' },
  { id: 10749, name: 'Romance' },
  { id: 878,   name: 'Science Fiction' },
  { id: 10770, name: 'TV Movie' },
  { id: 53,    name: 'Thriller' },
  { id: 10752, name: 'War' },
  { id: 37,    name: 'Western' },
];

// --- Styled components ---

const PageLayout = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;

const Sidebar = styled.nav`
  width: 230px;
  flex-shrink: 0;
  background: var(--color-surface);
  border-radius: 12px;
  box-shadow: var(--shadow-card);
  padding: 1.25rem;
  position: sticky;
  top: 1rem;
`;

const SidebarTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--color-border);
`;

const SidebarLink = styled.a`
  display: block;
  padding: 0.5rem 0;
  color: var(--color-text-muted);
  text-decoration: none;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid var(--color-border-subtle);
  transition: color 0.15s;

  &:hover { color: var(--color-accent); }
  &:last-child { border-bottom: none; }

  [data-theme="dark"] & { color: rgba(255, 255, 255, 0.90); }
  [data-theme="dark"] &:hover { color: var(--color-accent); }
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 680px;
`;

const Title = styled.h1`
  color: var(--color-text);
  margin-bottom: 1.5rem;
`;

const Section = styled.div`
  background: var(--color-surface);
  padding: 2rem;
  border-radius: 12px;
  box-shadow: var(--shadow-card);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: var(--color-text);
  margin: 0 0 0.5rem 0;
  font-size: 1.3rem;
`;

const PlatformGroupLabel = styled.h3`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-muted);
  margin: 1.25rem 0 0.6rem 0;

  &:first-of-type { margin-top: 0; }
`;

const Description = styled.p`
  color: var(--color-text-secondary);
  margin: 0 0 1.5rem 0;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const OptionCard = styled.label<{ $selected: boolean; $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 2px solid ${props =>
    props.$selected ? (props.$danger ? '#e53e3e' : 'var(--color-accent)') : 'var(--color-border)'};
  background: ${props =>
    props.$selected ? (props.$danger ? '#fff5f5' : 'var(--color-accent-subtle)') : 'var(--color-surface)'};
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  font-weight: ${props => props.$selected ? '600' : '400'};
  color: ${props => props.$selected ? (props.$danger ? '#c53030' : 'var(--color-text)') : 'var(--color-text-secondary)'};

  &:hover {
    border-color: ${props => props.$danger ? '#e53e3e' : 'var(--color-accent)'};
    background: ${props => props.$danger ? '#fff5f5' : 'var(--color-accent-subtle)'};
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: var(--color-accent);
  cursor: pointer;
  flex-shrink: 0;
`;

const Note = styled.p`
  color: var(--color-text-muted);
  font-size: 0.85rem;
  font-style: italic;
  margin: 0 0 1rem 0;
`;

const SaveButton = styled.button`
  background: var(--color-accent);
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: var(--color-accent-hover);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: #c6f6d5;
  color: #276749;
  border-radius: 8px;
  font-size: 0.95rem;
`;

const ErrorMessage = styled.div`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: #fed7d7;
  color: #c53030;
  border-radius: 8px;
  font-size: 0.95rem;
`;

const DangerSection = styled(Section)`
  border: 1px solid #feb2b2;
`;

const DangerButton = styled.button`
  background: #fff5f5;
  color: #c53030;
  border: 2px solid #e53e3e;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #fed7d7;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  background: var(--color-border);
  border-radius: 4px;
  height: 8px;
  overflow: hidden;
  margin-top: 1rem;
`;

const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  background: var(--color-accent);
  border-radius: 4px;
  width: ${p => p.$pct}%;
  transition: width 0.3s ease;
`;

const ProgressLabel = styled.div`
  font-size: 0.8rem;
  color: var(--color-text-muted);
  margin-top: 4px;
  text-align: center;
`;

// --- Component ---

const SettingsPage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [selectedProviders, setSelectedProviders] = useState<number[]>([]);
  const [selectedRatings, setSelectedRatings] = useState<string[]>([...MPAA_RATINGS]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [excludedGenres, setExcludedGenres] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [regeneratePhase, setRegeneratePhase] = useState<'generating' | 'refreshing-providers' | null>(null);
  const [regenerateProgress, setRegenerateProgress] = useState<{ done: number; total: number } | null>(null);
  const [regenerateMethodLabel, setRegenerateMethodLabel] = useState('');
  const [regenerateMessage, setRegenerateMessage] = useState('');
  const regenerateCancelRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    if (user.movie_providers) setSelectedProviders(user.movie_providers);
    // content_ratings: empty in DB = all allowed → show all checked
    setSelectedRatings(user.content_ratings?.length ? user.content_ratings : [...MPAA_RATINGS]);
    setSelectedLanguages(user.original_languages ?? []);
    setExcludedGenres(user.excluded_genres ?? []);
  }, [user]);

  const toggle = <T,>(list: T[], item: T): T[] =>
    list.includes(item) ? list.filter(x => x !== item) : [...list, item];

  const handleSave = async () => {
    setSaving(true);
    setSuccessMessage('');
    setError('');
    try {
      // Store empty array when all ratings are selected (= no filtering)
      const ratingsToSave = selectedRatings.length === MPAA_RATINGS.length ? [] : selectedRatings;
      await apiService.updateUserSettings({
        movie_providers: selectedProviders,
        content_ratings: ratingsToSave,
        original_languages: selectedLanguages,
        excluded_genres: excludedGenres,
      });
      await refreshUser();
      setSuccessMessage('Settings saved!');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const REGENERATE_METHODS: Array<{ value: 'similar' | 'director' | 'actor' | 'studio'; label: string }> = [
    { value: 'similar', label: 'Similar Movies' },
    { value: 'director', label: 'Director' },
    { value: 'actor', label: 'Actor/Actress' },
    { value: 'studio', label: 'Studio' },
  ];

  const handleRegenerate = async () => {
    if (!window.confirm(
      'This will clear all your current (unwatched, non-rejected) recommendations and generate a fresh batch across every discovery method (Similar, Director, Actor/Actress, Studio), then fetch streaming availability for them. Movies you already marked watched or rejected will not resurface. Continue?'
    )) {
      return;
    }

    regenerateCancelRef.current = false;
    setRegenerating(true);
    setRegenerateMessage('');
    setError('');
    setRegenerateProgress(null);
    let totalCount = 0;
    let totalBoosted = 0;
    let totalProviderUpdates = 0;
    try {
      await apiService.clearSuggestions();

      setRegeneratePhase('generating');
      for (const { value: methodValue, label } of REGENERATE_METHODS) {
        if (regenerateCancelRef.current) break;
        setRegenerateMethodLabel(label);
        let seedOffset = 0;
        while (!regenerateCancelRef.current) {
          const result = await apiService.generateRecommendations(seedOffset, methodValue);
          totalCount += result.count;
          totalBoosted += result.boosted ?? 0;
          if (result.total_seeds > 0) {
            setRegenerateProgress({ done: result.processed_seeds, total: result.total_seeds });
          }
          if (!result.has_more) break;
          seedOffset = result.processed_seeds;
        }
      }

      if (!regenerateCancelRef.current) {
        setRegeneratePhase('refreshing-providers');
        setRegenerateProgress(null);
        let providerOffset = 0;
        while (!regenerateCancelRef.current) {
          const result = await apiService.refreshProviders(providerOffset, true);
          totalProviderUpdates += result.updated;
          setRegenerateProgress({ done: result.processed, total: result.total });
          if (!result.has_more) break;
          providerOffset = result.processed;
        }
      }

      const boostSuffix = totalBoosted > 0 ? `, boosted ${totalBoosted} (recommended by more than one movie)` : '';
      const msg = regenerateCancelRef.current
        ? `Cancelled — generated ${totalCount} new recommendations${boostSuffix}, updated providers for ${totalProviderUpdates}`
        : `Regenerated recommendations — ${totalCount} new suggestions${boostSuffix}, providers fetched for ${totalProviderUpdates}`;
      setRegenerateMessage(msg);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to regenerate recommendations');
    } finally {
      setRegenerating(false);
      setRegeneratePhase(null);
      setRegenerateProgress(null);
      setRegenerateMethodLabel('');
    }
  };

  const handleRegenerateCancel = () => { regenerateCancelRef.current = true; };

  return (
    <>
      <PageLayout>
        <Sidebar>
          <SidebarTitle>Settings</SidebarTitle>
          <SidebarLink href="#streaming">Streaming Platforms</SidebarLink>
          <SidebarLink href="#ratings">Content Ratings</SidebarLink>
          <SidebarLink href="#language">Original Language</SidebarLink>
          <SidebarLink href="#genres">Exclude Genres</SidebarLink>
          <SidebarLink href="#danger-zone">Danger Zone</SidebarLink>
        </Sidebar>
        <Content>

      {/* Streaming Platforms */}
      <Section id="streaming">
        <Description>
          Select the platforms you subscribe to. When generating recommendations,
          only movies available on these services will be prioritised.
          Leave all unchecked to get recommendations regardless of platform.
        </Description>
        <PlatformGroupLabel>Subscription</PlatformGroupLabel>
        <OptionGrid>
          {SUBSCRIPTION_PLATFORMS.map(p => (
            <OptionCard key={p.id} $selected={selectedProviders.includes(p.id)}>
              <Checkbox
                type="checkbox"
                checked={selectedProviders.includes(p.id)}
                onChange={() => setSelectedProviders(prev => toggle(prev, p.id))}
              />
              {p.name}
            </OptionCard>
          ))}
        </OptionGrid>
        <PlatformGroupLabel>Free (Ad-Supported)</PlatformGroupLabel>
        <OptionGrid>
          {FREE_PLATFORMS.map(p => (
            <OptionCard key={p.id} $selected={selectedProviders.includes(p.id)}>
              <Checkbox
                type="checkbox"
                checked={selectedProviders.includes(p.id)}
                onChange={() => setSelectedProviders(prev => toggle(prev, p.id))}
              />
              {p.name}
            </OptionCard>
          ))}
        </OptionGrid>
        {selectedProviders.length === 0 && (
          <Note>No platforms selected — recommendations will include movies from all platforms.</Note>
        )}
      </Section>

      {/* Content Ratings */}
      <Section id="ratings">
        <Description>
          Choose which MPAA ratings to include in your recommendations.
          Uncheck a rating to exclude movies with that classification.
          Note: titles with no MPAA rating on record are still shown, since the filter
          can't be applied to unknown data.
        </Description>
        <OptionGrid style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))' }}>
          {MPAA_RATINGS.map(r => (
            <OptionCard key={r} $selected={selectedRatings.includes(r)}>
              <Checkbox
                type="checkbox"
                checked={selectedRatings.includes(r)}
                onChange={() => setSelectedRatings(prev => toggle(prev, r))}
              />
              {r}
            </OptionCard>
          ))}
        </OptionGrid>
        {selectedRatings.length === MPAA_RATINGS.length && (
          <Note>All ratings selected — no rating filter applied.</Note>
        )}
        {selectedRatings.length === 0 && (
          <Note style={{ color: '#c53030' }}>No ratings selected — no movies will match.</Note>
        )}
      </Section>

      {/* Original Language */}
      <Section id="language">
        <Description>
          Restrict recommendations to movies originally filmed in these languages.
          Leave all unchecked to include movies in any language.
        </Description>
        <OptionGrid>
          {LANGUAGES.map(l => (
            <OptionCard key={l.code} $selected={selectedLanguages.includes(l.code)}>
              <Checkbox
                type="checkbox"
                checked={selectedLanguages.includes(l.code)}
                onChange={() => setSelectedLanguages(prev => toggle(prev, l.code))}
              />
              {l.name}
            </OptionCard>
          ))}
        </OptionGrid>
        {selectedLanguages.length === 0 && (
          <Note>No languages selected — movies in any language will be included.</Note>
        )}
      </Section>

      {/* Excluded Genres */}
      <Section id="genres">
        <Description>
          Check any genres you never want recommended. Documentaries and TV Movies
          are common exclusions if you want only theatrical fiction films.
        </Description>
        <OptionGrid>
          {GENRES.map(g => (
            <OptionCard key={g.id} $selected={excludedGenres.includes(g.id)} $danger={excludedGenres.includes(g.id)}>
              <Checkbox
                type="checkbox"
                checked={excludedGenres.includes(g.id)}
                onChange={() => setExcludedGenres(prev => toggle(prev, g.id))}
                style={{ accentColor: excludedGenres.includes(g.id) ? '#e53e3e' : '#667eea' }}
              />
              {g.name}
            </OptionCard>
          ))}
        </OptionGrid>
        {excludedGenres.length === 0 && (
          <Note>No genres excluded — all genres may appear in recommendations.</Note>
        )}
      </Section>

      <SaveButton onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save Settings'}
      </SaveButton>

      {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
      {error && <ErrorMessage>{error}</ErrorMessage>}

      {/* Danger Zone */}
      <DangerSection id="danger-zone">
        <SectionTitle>Danger Zone</SectionTitle>
        <Description>
          Completely regenerate your recommendations. This clears your current
          unwatched, non-rejected suggestions, builds a fresh batch from your rated movies,
          and fetches streaming availability for the new suggestions.
          Movies you've already marked watched or rejected stay excluded and won't resurface.
        </Description>
        <DangerButton onClick={handleRegenerate} disabled={regenerating}>
          {regenerating ? 'Regenerating…' : 'Regenerate Recommendations'}
        </DangerButton>
        {regenerating && (
          <button
            onClick={handleRegenerateCancel}
            style={{ marginLeft: '0.75rem', background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Cancel
          </button>
        )}
        {regenerateProgress && (
          <>
            <ProgressBar>
              <ProgressFill $pct={Math.round((regenerateProgress.done / regenerateProgress.total) * 100)} />
            </ProgressBar>
            <ProgressLabel>
              {regeneratePhase === 'refreshing-providers'
                ? `Fetching streaming availability… ${regenerateProgress.done} / ${regenerateProgress.total} suggestions`
                : `Fetching (${regenerateMethodLabel})… ${regenerateProgress.done} / ${regenerateProgress.total} seed movies`}
            </ProgressLabel>
          </>
        )}
        {regenerateMessage && <SuccessMessage>{regenerateMessage}</SuccessMessage>}
      </DangerSection>

        </Content>
      </PageLayout>
    </>
  );
};

export default SettingsPage;
